'use client';

import { useEffect, useMemo, useRef } from 'react';
import {
	BN,
	BASE_PRECISION_EXP,
	BigNum,
	PositionDirection,
	UserAccount,
} from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';

export default function useEmitPositionFillDeltas() {
	const connected = useWalletIsConnected();
	const driftReady = useDriftClientIsReady();
	const accounts = useLazySubAccounts();
	const appEventEmitter = useDriftStore((s) => s.appEventEmitter);

	const prevBasesRef = useRef<Map<string, Map<number, BN>>>(new Map());
	const subFlags = useMemo(
		() => accounts.map((a) => !!a.client?.isSubscribed).join(','),
		[accounts]
	);

	const enabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_POSITION_DELTA_TOAST_MATCHING')
	);

	useEffect(() => {
		if (!connected || !driftReady || !enabled) return;

		const unsubs = accounts.map((acct) => {
			const user = acct.client;
			if (!user || !user.isSubscribed) return;

			const userKey = acct.userKey;
			if (!prevBasesRef.current.has(userKey)) {
				const init = new Map<number, BN>();
				user.getUserAccountOrThrow().perpPositions.forEach((p) => {
					init.set(p.marketIndex, p.baseAssetAmount);
				});
				prevBasesRef.current.set(userKey, init);
			}

			const handler = (userAccount: UserAccount) => {
				const current = userAccount;
				const prevForUser =
					prevBasesRef.current.get(userKey) ?? new Map<number, BN>();

				// we need this check because this array can have duplicates for market index 0(SOL-PERP)
				const processedMarketIndexes = new Set<number>();

				// Syncing fills for perp markets
				current.perpPositions.forEach((p) => {
					if (processedMarketIndexes.has(p.marketIndex)) return;
					processedMarketIndexes.add(p.marketIndex);

					const prev = prevForUser.get(p.marketIndex) ?? new BN(0);
					const diff = p.baseAssetAmount.sub(prev);
					if (diff.isZero()) return;

					const direction = diff.gt(new BN(0))
						? PositionDirection.LONG
						: PositionDirection.SHORT;
					const baseDelta = BigNum.from(diff.abs(), BASE_PRECISION_EXP);

					appEventEmitter.emit('positionFillDelta', {
						marketId: MarketId.createPerpMarket(p.marketIndex),
						baseDelta,
						direction,
						ts: Date.now(),
					});
				});

				const next = new Map<number, BN>();
				current.perpPositions.forEach((p) => {
					if (!next.has(p.marketIndex)) {
						next.set(p.marketIndex, p.baseAssetAmount);
					}
				});
				prevBasesRef.current.set(userKey, next);
			};

			user.eventEmitter.on('userAccountUpdate', handler);

			return () => {
				user.eventEmitter.removeListener('userAccountUpdate', handler);
			};
		});

		return () => {
			unsubs.forEach((u) => u?.());
		};
	}, [accounts, connected, driftReady, subFlags, appEventEmitter, enabled]);
}
